import type { ThunkAction } from "redux-thunk";
import type { RootState } from "~/app/store";
import type { SalesPartnerContextData } from "~/domains/affiliate/types/models";
import {
  deletePreferredPartnerCookie,
  deleteSelectedSubsidiaryCookie,
  savePreferredPartnerCookie,
  saveSelectedSubsidiaryCookie,
} from "~/domains/affiliate/utils/utils";
import { apiDeletePreferredPartner, apiSavePreferredPartner } from "~/domains/affiliate/api/broker";
import { userIsRegistered } from "~/domains/user/utils/roles";

export const CLEAR_SALES_PARTNER = "Clear sales partner";
export const CHANGE_SALES_PARTNER = "Change sales partner";
export const UPDATE_SUBSIDIARY = "Update subsidiary";
export const REPLACE_SALES_PARTNER_CONTEXT = "Replace sales partner context";

export type AffiliateMutationOptions = {
  expirationTimeHours?: number;
};

export function replaceSalesPartnerContext(context?: SalesPartnerContextData) {
  return {
    type: REPLACE_SALES_PARTNER_CONTEXT,
    payload: context,
  } as const;
}

// export function saveVisitedSalesPartner() {
//   return {
//     type: SAVE_VISITED_SALES_PARTNER,
//   } as const;
// }

function changePreferredSalesPartner(affiliate: SalesPartnerDTO, subsidiaryId?: string) {
  return {
    type: CHANGE_SALES_PARTNER,
    payload: { affiliate, subsidiaryId },
  } as const;
}

function clearPreferredSalesPartner() {
  return {
    type: CLEAR_SALES_PARTNER,
  } as const;
}

function updateSubsidiary(subsidiaryId?: string) {
  return {
    type: UPDATE_SUBSIDIARY,
    payload: subsidiaryId,
  } as const;
}

export type AffiliateActions =
  | ReturnType<typeof changePreferredSalesPartner>
  | ReturnType<typeof clearPreferredSalesPartner>
  | ReturnType<typeof updateSubsidiary>
  | ReturnType<typeof replaceSalesPartnerContext>;

export function saveAffiliateThunk({
  salesPartnerId,
  expirationTimeHours,
  ...affiliate
}: SalesPartnerDTO & AffiliateMutationOptions & { salesPartnerId?: string }): ThunkAction<
  Promise<boolean>,
  RootState,
  unknown,
  AffiliateActions
> {
  return async (dispatch, getState) => {
    const { savedAffiliate } = getState().affiliate;
    const sameAffiliate = savedAffiliate?.affiliateId === affiliate.affiliateId;
    if (!sameAffiliate) {
      // Different affiliate, save it
      return dispatch(internalSaveAffiliateThunk({ ...affiliate, salesPartnerId, expirationTimeHours }));
    } else if (salesPartnerId) {
      // Same affiliate, just update the subsidiary
      return dispatch(internalUpdateSubsidiaryThunk(salesPartnerId, { expirationTimeHours }));
    }
    return false; // No changes
  };
}

export function undoSaveAffiliateThunk(
  options?: AffiliateMutationOptions,
): ThunkAction<Promise<boolean>, RootState, unknown, AffiliateActions> {
  return (dispatch, getState) => {
    const { previousSavedAffiliate, previousSavedSubsidiaryId } = getState().affiliate;
    if (previousSavedAffiliate) {
      return dispatch(
        saveAffiliateThunk({
          ...previousSavedAffiliate,
          salesPartnerId: previousSavedSubsidiaryId,
          expirationTimeHours: options?.expirationTimeHours,
        }),
      );
    } else {
      return dispatch(clearSavedAffiliateThunk());
    }
  };
}

export function clearSavedAffiliateThunk(): ThunkAction<Promise<boolean>, RootState, unknown, AffiliateActions> {
  return async (dispatch) => {
    try {
      if (userIsRegistered()) {
        const response = await apiDeletePreferredPartner();
        if (!response.ok) {
          console.warn(`API call to delete preferred partner failed: ${response.status}`);
          return false;
        }
      }
      deletePreferredPartnerCookie();
      deleteSelectedSubsidiaryCookie();
      dispatch(clearPreferredSalesPartner());
      return true;
    } catch (e) {
      console.error(e);
      return false;
    }
  };
}

export function clearSavedSubsidiaryThunk(
  options?: AffiliateMutationOptions,
): ThunkAction<Promise<boolean>, RootState, unknown, AffiliateActions> {
  return internalUpdateSubsidiaryThunk(undefined, options);
}

export function saveVisitedSalesPartnerThunk(
  options?: AffiliateMutationOptions,
): ThunkAction<Promise<boolean>, RootState, unknown, AffiliateActions> {
  return async (dispatch, getState) => {
    const visitedAffiliate = getState().affiliate.visitedAffiliate;
    if (visitedAffiliate) {
      return dispatch(saveAffiliateThunk({ ...visitedAffiliate, expirationTimeHours: options?.expirationTimeHours }));
    }
    return false;
  };
}

// Only updates the subsidiary. Assumes the affiliate is the same
function internalUpdateSubsidiaryThunk(
  salesPartnerId?: string,
  options?: AffiliateMutationOptions,
): ThunkAction<Promise<boolean>, RootState, unknown, AffiliateActions> {
  return async (dispatch, getState) => {
    const { savedAffiliate } = getState().affiliate;
    if (!savedAffiliate) {
      return false;
    }

    try {
      if (userIsRegistered()) {
        const result = await apiSavePreferredPartner({ affiliateId: savedAffiliate.affiliateId, salesPartnerId });
        if (!result.ok) {
          return false;
        }
      }
      if (salesPartnerId) {
        saveSelectedSubsidiaryCookie(salesPartnerId, options?.expirationTimeHours);
        dispatch(updateSubsidiary(salesPartnerId));
      } else {
        deleteSelectedSubsidiaryCookie();
        dispatch(updateSubsidiary());
      }
      return true;
    } catch (e) {
      console.error(e);
      return false;
    }
  };
}

// Updates the affiliate and subsidiary (if also provided)
function internalSaveAffiliateThunk({
  salesPartnerId,
  expirationTimeHours,
  ...affiliate
}: SalesPartnerDTO & AffiliateMutationOptions & { salesPartnerId?: string }): ThunkAction<
  Promise<boolean>,
  RootState,
  unknown,
  AffiliateActions
> {
  return async (dispatch) => {
    const affiliateId = affiliate.affiliateId;
    try {
      // Api save for registered...
      if (userIsRegistered()) {
        const result = await apiSavePreferredPartner({ affiliateId, salesPartnerId });
        if (!result.ok) {
          console.warn(`API call to save preferred partner failed: ${result.status}`);
          return false;
        }
      }
      // But also always save in the cookies
      savePreferredPartnerCookie(affiliateId, expirationTimeHours);
      if (salesPartnerId) {
        saveSelectedSubsidiaryCookie(salesPartnerId, expirationTimeHours);
      } else {
        deleteSelectedSubsidiaryCookie();
      }

      dispatch(changePreferredSalesPartner(affiliate, salesPartnerId));
      return true;
    } catch (e) {
      console.error(e);
      return false;
    }
  };
}

/*
    const affiliateId = affiliate.affiliateId;
    try {
      // Api save for registered...
      if (userIsRegistered()) {
        const result = await apiSavePreferredPartner({ affiliateId, salesPartnerId });
        if (!result.ok) {
          console.warn(`API call to save preferred partner failed: ${result.status}`);
          return false;
        }
      }
      // But also always save in the cookies
      savePreferredPartnerCookie(affiliateId);
      if (salesPartnerId) {
        saveSelectedSubsidiaryCookie(salesPartnerId);
      } else {
        deleteSelectedSubsidiaryCookie();
      }

      dispatch(changePreferredSalesPartner(affiliate, salesPartnerId));
      return true;
    } catch (e) {
      console.error(e);
      return false;
    }
 */
